import TeamLogo from '../../templates/TeamLogo';
import ResponsiveTeamName from '../../templates/ResponsiveTeamName';
import { formatFixtureDate } from '../../helpers/dateUtils';


// A miss is shown in neutral grey rather than red: it's still a published call, and a
// column of red pills read as a failure list on a page meant to build trust.
const STATUS_CLASSES = {
    EXACT: 'bg-sk-success-20 border-sk-success-40 text-sk-green',
    RESULT: 'bg-sk-success-20 border-sk-success-40 text-sk-green',
    MISS: 'bg-sk-neutral-20 border-sk-neutral-40 text-white-80',
};

const outcomeOf = (home, away) => (home === away ? 'draw' : home > away ? 'home' : 'away');

// The feed returns the three likeliest scorelines; the first is the one we publish as
// "our prediction", which is how every other surface reads it (MarketPredictedCard,
// FixtureAccordionCard, the fixture detail pages). `score` is parsed as a fallback for
// payloads that carry the string but not the split home/away numbers.
const predictedScore = (row) => {
    const top = row?.prediction?.top3_scorelines?.[0];
    if (!top) return null;

    const [fromString, ...rest] = String(top.score ?? '').split('-');
    const home = top.home ?? Number.parseInt(fromString, 10);
    const away = top.away ?? Number.parseInt(rest[0], 10);

    return Number.isFinite(home) && Number.isFinite(away) ? { home, away } : null;
};

// actualHome/actualAway is the settled full-time score (it already resolves ET and
// penalties — see FremiumFixture's finalScore), so it's what a prediction is graded on.
const actualScore = (row) => {
    const home = row?.matchResult?.actualHome;
    const away = row?.matchResult?.actualAway;

    return home == null || away == null ? null : { home, away };
};

// EXACT: we called the scoreline. RESULT: right outcome, wrong scoreline. MISS: the
// match went the other way entirely.
//
// This is derived, not read from the payload: a row's `status` is the match's own
// status ("finished"), not a verdict on the prediction.
const gradeRow = (predicted, actual) => {
    if (!predicted || !actual) return null;
    if (predicted.home === actual.home && predicted.away === actual.away) return 'EXACT';

    return outcomeOf(predicted.home, predicted.away) === outcomeOf(actual.home, actual.away) ? 'RESULT' : 'MISS';
};

// Newest first, and only rows we can actually score: a match with no published
// prediction, or no final score yet, has nothing to show in a scorecard.
export const gradeResults = (rows) => (rows ?? [])
    .map((row) => {
        const predicted = predictedScore(row);
        const actual = actualScore(row);
        return { row, predicted, actual, status: gradeRow(predicted, actual) };
    })
    .filter((entry) => entry.status)
    .sort((a, b) => new Date(b.row.match_date) - new Date(a.row.match_date));

const ScoreLine = ({ label, home, away }) => (
    <div className="d-flex align-items-end justify-content-end gap-1 mb-1">
        <span className="extra-extra-small text-white-80">{label}</span>
        <span className="sk-result-score extra-small fw-semi-bold text-white text-end">{home}-{away}</span>
    </div>
);

const TeamLine = ({ team }) => (
    <div className="d-flex align-items-center gap-2 overflow-hidden mb-1">
        <TeamLogo
            team={team}
            alt={team?.name}
            className={`disc-35 object-fit-contain flex-shrink-0`}
        />
        <span className="small fw-semi-bold text-white text-truncate">
            <ResponsiveTeamName team={team} maxSize="shortened" />
        </span>
    </div>
);

const RowSkeleton = () => (
    <div className="sk-result-row d-flex align-items-center justify-content-between gap-2" aria-hidden="true">
        <div className="d-flex flex-column gap-1">
            <span className="shimmer bg-dark-mode-700 rounded-2 d-block" style={{ width: 110, height: 16 }} />
            <span className="shimmer bg-dark-mode-700 rounded-2 d-block" style={{ width: 92, height: 16 }} />
            <span className="shimmer bg-dark-mode-700 rounded-2 d-block" style={{ width: 66, height: 11 }} />
        </div>
        <div className="sk-result-meta d-flex align-items-center justify-content-between gap-2 flex-shrink-0">
            <span className="shimmer bg-dark-mode-700 rounded-2" style={{ width: 62, height: 30 }} />
            <span className="shimmer bg-dark-mode-700 rounded-pill flex-shrink-0" style={{ width: 48, height: 19 }} />
        </div>
    </div>
);

const SKELETON_ROWS = 5;

// `entries` is the output of gradeResults, already scoped to the selected league by
// TrackRecord (which owns the one fetch for every league); null while that's loading.
const PredictionResultsCard = ({ entries, className = '' }) => {
    const loading = entries === null || entries === undefined;

    if (!loading && entries.length === 0) return null;

    return (
        <div className={`sk-result-card w-100 rounded-5 p-md-4 p-3 d-flex flex-column gap-2 overflow-hidden ${className}`.trim()}>
            {loading
                ? Array.from({ length: SKELETON_ROWS }).map((_, index) => <RowSkeleton key={index} />)
                : entries.map(({ row, predicted, actual, status }, index) => (
                    <div key={row?.fixture_id ?? index} className="sk-result-row d-flex align-items-center justify-content-between gap-2">
                        <div className="d-flex flex-column justify-content-center overflow-hidden">
                            <TeamLine team={row?.home_team} />
                            <TeamLine team={row?.away_team} />
                            {row?.match_date && (
                                <p className="extra-extra-small text-white-80 mb-0">{formatFixtureDate(row.match_date)}</p>
                            )}
                        </div>

                        <div className="sk-result-meta d-flex align-items-center justify-content-between gap-2 flex-shrink-0">
                            <div className="d-flex flex-column align-items-end">
                                <ScoreLine label="Predicted" home={predicted.home} away={predicted.away} />
                                <ScoreLine label="Result" home={actual.home} away={actual.away} />
                            </div>

                            <span className={`extra-extra-small border rounded-pill px-2 py-1 text-nowrap ${STATUS_CLASSES[status]}`}>
                                {status}
                            </span>
                        </div>
                    </div>
                ))}
        </div>
    );
};

export default PredictionResultsCard;
